import { Account } from '@/common/types'
import { AccountType } from '@/common/types'

/**
 * Pure function that resolves which account should be active. No side effects, fully testable.
 *
 * Preference order: `currentActive`, then `userAccountId`, then `primaryAccount`. A candidate is
 * skipped if it doesn't exist, has failed, or is the adviser's own account on a route that isn't
 * allowed to show it (`allowPrimaryAdviserAccount`).
 *
 * If none of those match, land on the adviser's own account anyway rather than an arbitrary
 * other one - unless `userAccountId` points at some other real account, in which case that
 * account is tried instead. Advisers usually have no `userAccountId` of their own, so this is
 * their normal landing spot; route guards redirect it to the adviser workspace (/clients).
 *
 * Failing that, fall back to the first selectable account in `accounts`.
 */
export function resolveActiveAccount(options: {
    currentActive: string | null
    userAccountId: string | null
    primaryAccount: Account | null
    accounts: Account[]
    failedAccountIds: Set<string>
    allowPrimaryAdviserAccount?: boolean
}): string | null {
    const {
        currentActive,
        userAccountId,
        primaryAccount,
        accounts,
        failedAccountIds,
        allowPrimaryAdviserAccount = false,
    } = options

    const primaryAccountId = primaryAccount?.accountId ?? null
    const isAdviserPrimary = primaryAccount?.accountType === AccountType.ADVISER

    const isBlockedAdviserPrimary = (accountId: string | null): boolean =>
        isAdviserPrimary && !allowPrimaryAdviserAccount && accountId === primaryAccountId

    const exists = (accountId: string | null): boolean =>
        accountId !== null && (accountId === primaryAccountId || accounts.some((a) => a.accountId === accountId))

    const isSelectable = (accountId: string | null): boolean =>
        exists(accountId) && !failedAccountIds.has(accountId as string) && !isBlockedAdviserPrimary(accountId)

    const preferred = [currentActive, userAccountId, primaryAccountId].find(isSelectable)
    if (preferred) return preferred

    // The adviser's own account is a safe landing spot when userAccountId isn't pointing at some
    // other real account instead - it's null (the common case for advisers) or the same account.
    const hasOtherOwnAccount = userAccountId !== null && userAccountId !== primaryAccountId
    if (isAdviserPrimary && !hasOtherOwnAccount && primaryAccountId && !failedAccountIds.has(primaryAccountId)) {
        return primaryAccountId
    }

    return accounts.find((a) => isSelectable(a.accountId))?.accountId ?? null
}
